import { useEffect, useMemo, useState } from "react";
import { openInvoicePdf } from "../utils/openPdf";

import { fetchInvoiceApprovalWorkflows } from "../api";

const APPROVER_TABS = [
    { key: "pending", label: "Pending Action" },
    { key: "hold", label: "On Hold" },
    { key: "rejected", label: "Rejected" },
];

const ACTION_OPTIONS = [
    "Approve with Deduction",
    "Approve with No Deduction",
    "Raise Clarification",
    "Hold & Transfer",
    "Reject",
    "Edit Conditions",
];

function formatAmount(numberValue) {
    if (numberValue == null) return "-";
    const n = typeof numberValue === "number" ? numberValue : parseFloat(numberValue) || 0;
    return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function conditionRowsFromWorkflow(workflow) {
    const values = workflow.conditionValues?.values || {};
    const sourceDebug = workflow.conditionValues?.sourceDebug || {};
    return Object.keys(values).map((key) => ({
        name: sourceDebug[key]?.name || key,
        value: values[key] || "-",
    }));
}

function workflowToApproverRow(workflow) {
    return {
        id: `workflow-${workflow.id}`,
        workflowId: workflow.id,
        invoiceId: workflow.invoiceId || workflow.id,
        invoiceNo: workflow.invoiceNo || "-",
        vendorNameOcr: workflow.vendorNameOcr || "-",
        vendorNameMaster: workflow.vendorNameMaster || "-",
        vendorCode: workflow.vendorCode || "-",
        invoiceDate: workflow.invoiceDate || "-",
        invoiceAmount: workflow.invoiceAmount || "",
        pdf: workflow.uniqueName || "View PDF",
        materialOrService: workflow.materialOrService || "-",
        invoiceType: workflow.invoiceType || "-",
        queue: "pending",
        highRisk: "Normal",
        currentApprover: workflow.currentApprover || "-",
        nextApprover: workflow.currentApprover || "-",
        conditions: conditionRowsFromWorkflow(workflow),
        remarks: "Submitted by Initiator and pending approver action.",
        //previousTransactions: [],
        previousTransactions: Array.isArray(workflow.previousTransactions)
            ? workflow.previousTransactions
            : [],

        auditTrail: ["Submitted to workflow", `Pending with ${workflow.currentApprover || "approver"}`],
        highRiskReasons: [],
        matchedApprovalPath: workflow.matchedApprovalPath || {},
        rawWorkflow: workflow,
    };
}

/*
const DUMMY_APPROVER_ROWS = [
    {
        id: 1,
        invoiceId: "1",
        invoiceNo: "INV-1001",
        vendorNameOcr: "Acme Corp OCR",
        vendorNameMaster: "Acme Corporation",
        vendorCode: "ACME01",
        invoiceDate: "2026-06-10",
        invoiceAmount: 7200,
        pdf: "INV-1001.pdf",
        materialOrService: "Material",
        invoiceType: "PO",
        queue: "pending",
        highRisk: "Low Risk",
        currentApprover: "Alice",
        nextApprover: "Bob",
        conditions: [{ name: "PO Number", value: "9500011224" }, { name: "PO Prefix", value: "PO-Series-9500" }],
        remarks: "Sample invoice",
        previousTransactions: [],
        auditTrail: ["Submitted", "Reviewed"],
        highRiskReasons: [],
    },
    {
        id: 4,
        invoiceId: "4",
        invoiceNo: "INV-1004",
        vendorNameOcr: "Delta Traders OCR",
        vendorNameMaster: "Delta Traders",
        vendorCode: "DEL04",
        invoiceDate: "2026-06-13",
        invoiceAmount: 4300,
        pdf: "INV-1004.pdf",
        materialOrService: "Service",
        invoiceType: "Non-PO",
        queue: "rejected",
        highRisk: "Low Risk",
        currentApprover: "Grace",
        nextApprover: "-",
        conditions: [],
        remarks: "Rejected for test",
        previousTransactions: [],
        auditTrail: ["Submitted", "Rejected"],
        highRiskReasons: [],
    },
    {
        id: 3,
        invoiceId: "3",
        invoiceNo: "INV-1003",
        vendorNameOcr: "Gamma Services OCR",
        vendorNameMaster: "Gamma Services",
        vendorCode: "GAM03",
        invoiceDate: "2026-06-12",
        invoiceAmount: 9800,
        pdf: "INV-1003.pdf",
        materialOrService: "Material",
        invoiceType: "PO",
        queue: "pending",
        highRisk: "High Risk",
        currentApprover: "Eve",
        nextApprover: "Frank",
        conditions: [{ name: "PO Number", value: "9500011225" }],
        remarks: "High risk test invoice",
        previousTransactions: [],
        auditTrail: ["Submitted", "Flagged for amount"],
        highRiskReasons: ["Amount mismatch", "Vendor unknown"],
    },
    {
        id: 2,
        invoiceId: "2",
        invoiceNo: "INV-1002",
        vendorNameOcr: "Beta Ltd OCR",
        vendorNameMaster: "Beta Limited",
        vendorCode: "BETA02",
        invoiceDate: "2026-06-11",
        invoiceAmount: 15000,
        pdf: "INV-1002.pdf",
        materialOrService: "Service",
        invoiceType: "Non-PO",
        queue: "hold",
        highRisk: "High Risk",
        currentApprover: "Charlie",
        nextApprover: "Dana",
        conditions: [{ name: "PO Number", value: "" }],
        remarks: "Needs verification",
        previousTransactions: [
            { invoiceNo: "PV-9001", invoiceDate: "2025-12-12", invoiceAmount: 14000, deduction: 0, invoiceType: "PO", materialOrService: "Service", pdf: "PV-9001.pdf" },
        ],
        auditTrail: ["Submitted", "Flagged"],
        highRiskReasons: ["Mismatch amount"],
    },
];
*/


function getActionFields(action) {
    if (action === "Approve with Deduction") {
        return ["Deduction Amount", "Deduction Reason", "Remarks"];
    }
    if (action === "Approve with No Deduction") {
        return ["Remarks"];
    }
    if (action === "Raise Clarification") {
        return ["Send Clarification To", "Clarification Text"];
    }
    if (action === "Hold & Transfer") {
        return ["Transfer To", "Hold Remarks"];
    }
    if (action === "Reject") {
        return ["Rejection Reason"];
    }
    if (action === "Edit Conditions") {
        return ["Condition 1", "Condition 2", "Condition Remarks"];
    }
    return [];
}

export default function ApproverDashboard() {
    const [activeTab, setActiveTab] = useState("pending");
    const [workflowRows, setWorkflowRows] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedAction, setSelectedAction] = useState("Approve with No Deduction");
    const [activeModal, setActiveModal] = useState("");

    // checklists implementation 1
    const [checklistItems, setChecklistItems] = useState([
        { name: "Invoice Number Match", checked: false },
        { name: "GST Check", checked: false },
        { name: "Amount Validation", checked: false },
    ]);
    
    const [loadingWorkflows, setLoadingWorkflows] = useState(false);
    const [workflowError, setWorkflowError] = useState("");
   
    // const allRows = [
    //     ...workflowRows,
    //     ...DUMMY_APPROVER_ROWS.filter((row) => row.queue !== "pending"),
    // ];

    const allRows = workflowRows;

    const visibleRows = allRows.filter((row) => row.queue === activeTab);
    const selectedRow = allRows.find((row) => row.id === selectedRowId) || visibleRows[0] || null;
    const actionFields = getActionFields(selectedAction);
    const tableColumnCount =
        12 + (activeTab === "hold" ? 2 : 0) + (activeTab === "rejected" ? 1 : 0);

    const summary = useMemo(
        () => ({
            pending: allRows.filter((row) => row.queue === "pending").length,
            hold: allRows.filter((row) => row.queue === "hold").length,
            rejected: allRows.filter((row) => row.queue === "rejected").length,
            highRisk: allRows.filter((row) => row.highRisk === "High Risk").length,
        }),
        [allRows],
    );

    async function loadPendingWorkflows() {
        setLoadingWorkflows(true);
        setWorkflowError("");
        try {
            const workflows = await fetchInvoiceApprovalWorkflows({ status: "PENDING_APPROVER" });
            const mappedRows = (Array.isArray(workflows) ? workflows : []).map(workflowToApproverRow);
            setWorkflowRows(mappedRows);
            setSelectedRowId((current) => {
                if (current && mappedRows.some((row) => row.id === current)) {
                    return current;
                }
                return mappedRows[0]?.id || null;
            });
        } catch (requestError) {
            setWorkflowError(requestError.message || "Unable to load pending approval workflows.");
        } finally {
            setLoadingWorkflows(false);
        }
    }

    useEffect(() => {
        loadPendingWorkflows();
    }, []);

    function selectTab(tabKey) {
        setActiveTab(tabKey);
        const firstRow = allRows.find((row) => row.queue === tabKey);
        setSelectedRowId(firstRow?.id || null);
    }

    function renderModalContent() {
        if (!selectedRow) return null;

        if (activeModal === "summary") {
            return (
                <div className="approver-detail-grid">
                    <label>
                        <span>Invoice No.</span>
                        <input type="text" value={selectedRow.invoiceNo} readOnly />
                    </label>
                    <label>
                        <span>Vendor OCR</span>
                        <input type="text" value={selectedRow.vendorNameOcr} readOnly />
                    </label>
                    <label>
                        <span>Vendor Master</span>
                        <input type="text" value={selectedRow.vendorNameMaster} readOnly />
                    </label>
                    <label>
                        <span>Vendor Code</span>
                        <input type="text" value={selectedRow.vendorCode} readOnly />
                    </label>
                    <label>
                        <span>Invoice Amount</span>
                        <input type="text" value={formatAmount(selectedRow.invoiceAmount)} readOnly />
                    </label>
                    <label>
                        <span>Invoice Date</span>
                        <input type="text" value={selectedRow.invoiceDate} readOnly />
                    </label>
                    <label>
                        <span>Invoice Type</span>
                        <input type="text" value={selectedRow.invoiceType} readOnly />
                    </label>
                    <label>
                        <span>Material / Service</span>
                        <input type="text" value={selectedRow.materialOrService} readOnly />
                    </label>
                    <label>
                        <span>Current Approver</span>
                        <input type="text" value={selectedRow.currentApprover || "-"} readOnly />
                    </label>
                    <label>
                        <span>Next Approver</span>
                        <input type="text" value={selectedRow.nextApprover || "-"} readOnly />
                    </label>
                </div>
            );
        }

        if (activeModal === "decision") {
            return (
                <div className="workspace-panel approver-review-card">
                    <div className="users-form__header">
                        <div>
                            <h3>Invoice Review Workspace</h3>
                            <p>Use this area to take approval action on the selected invoice.</p>
                        </div>
                    </div>

                    {selectedRow ? (
                        <>
                            <div className="approver-detail-grid">
                                <label>
                                    <span>Invoice No.</span>
                                    <input type="text" value={selectedRow.invoiceNo} readOnly />
                                </label>
                                <label>
                                    <span>Vendor Code</span>
                                    <input type="text" value={selectedRow.vendorCode} readOnly />
                                </label>
                                <label>
                                    <span>Invoice Amount</span>
                                    <input type="text" value={formatAmount(selectedRow.invoiceAmount)} readOnly />
                                </label>
                                <label>
                                    <span>Current Approver</span>
                                    <input type="text" value={selectedRow.currentApprover || "-"} readOnly />
                                </label>
                                <label>
                                    <span>Decision</span>
                                    <select value={selectedAction} onChange={(event) => setSelectedAction(event.target.value)}>
                                        {ACTION_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    <span>Next Approver</span>
                                    <input type="text" value={selectedRow.nextApprover || "-"} readOnly />
                                </label>
                            </div>

                            <div className="approver-action-fields">
                                {actionFields.map((field) => (
                                    <label key={field}>
                                        <span>{field}</span>
                                        {field.toLowerCase().includes("remarks") ||
                                            field.toLowerCase().includes("reason") ||
                                            field.toLowerCase().includes("text") ? (
                                            <textarea rows="3" placeholder={field} />
                                        ) : (
                                            <input type="text" placeholder={field} />
                                        )}
                                    </label>
                                ))}
                            </div>

                            <div className="approver-action-bar">
                                <button type="button" className="primary-button">
                                    Save Decision
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="config-note">Select an invoice to review.</div>
                    )}
                </div>
            );
        }

        if (activeModal === "context") {
            return (
                <div className="approver-detail-grid">
                    <label>
                        <span>Invoice No.</span>
                        <input type="text" value={selectedRow.invoiceNo} readOnly />
                    </label>
                    <label>
                        <span>Route</span>
                        <input type="text" value={selectedRow.invoiceType} readOnly />
                    </label>
                    <label>
                        <span>Type</span>
                        <input type="text" value={selectedRow.materialOrService} readOnly />
                    </label>
                    <label>
                        <span>Risk</span>
                        <input type="text" value={selectedRow.highRisk} readOnly />
                    </label>
                    <label>
                        <span>Invoice Amount</span>
                        <input type="text" value={formatAmount(selectedRow.invoiceAmount)} readOnly />
                    </label>
                    <label>
                        <span>PO Number</span>
                        <input type="text" value={(selectedRow.conditions || []).find((c) => c.name === "PO Number")?.value || "9500011224"} readOnly />
                    </label>
                    <label>
                        <span>PO Prefix</span>
                        <input type="text" value={(selectedRow.conditions || []).find((c) => c.name === "PO Prefix")?.value || "PO-Series-9500"} readOnly />
                    </label>
                    <label>
                        <span>Remarks</span>
                        <textarea rows="3" value={selectedRow.remarks} readOnly />
                    </label>
                </div>
            );
        }

        if (activeModal === "transactions") {
            return (
                <div className="config-table">
                    <table className="approver-modal-table">
                        <thead>
                            <tr>
                                <th>Invoice No.</th>
                                <th>Invoice Date</th>
                                <th>Invoice Amount</th>
                                <th>Deduction</th>
                                <th>PO / Non PO</th>
                                <th>Material / Service</th>
                                <th>PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(Array.isArray(selectedRow.previousTransactions) ? selectedRow.previousTransactions : []).map((transaction, index) => (
                                <tr key={index}>
                                    <td>{transaction.invoiceNo}</td>
                                    <td>{transaction.invoiceDate}</td>
                                    <td>{formatAmount(transaction.invoiceAmount)}</td>
                                    <td>{transaction.anyDeduction || "-"} </td>
                                    <td>{transaction.invoiceType}</td>
                                    <td>{transaction.materialOrService}</td>
                                    <td>
                                        <button type="button" className="text-button">
                                            {transaction.pdfPath ? "View PDF" : "-"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {Array.isArray(selectedRow.previousTransactions) && selectedRow.previousTransactions.length ? null : (
                                <tr>
                                    <td colSpan="7">No previous transactions found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
        }

        if (activeModal === "audit") {
            return (
                <div className="approver-modal-list">
                    {selectedRow.auditTrail.map((item, index) => (
                        <div key={item}>
                            <strong>Step {index + 1}</strong>
                            <span>{item}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="approver-modal-list">
                <div>
                    <strong>Invoice</strong>
                    <span>{selectedRow.invoiceNo}</span>
                </div>
                <div>
                    <strong>Risk Score</strong>
                    <span>{selectedRow.highRisk}</span>
                </div>
                <div>
                    <strong>Reasons</strong>
                    <ul className="approver-risk-list">
                        {(selectedRow.highRiskReasons || []).map((reason) => (
                            <li key={reason}>{reason}</li>
                        ))}
                        {selectedRow.highRiskReasons?.length ? null : (
                            <li>No high risk reason is configured for this invoice.</li>
                        )}
                    </ul>
                </div>
            </div>
        );
    }

    return (
        <section className="approver-dashboard">
            <div className="workspace-panel approver-dashboard__hero">
                <div>
                    {/* <p className="eyebrow">Invoice Approval</p> */}
                    <h2>Invoice Approval Approver Dashboard</h2>
                    <p className="config-view__intro">
                        Review pending invoices, manage holds, and inspect rejected cases from one approval workspace.
                    </p>
                </div>
                <div className="approver-summary">
                    <div className="approver-summary__card">
                        <span>Pending</span>
                        <strong>{summary.pending}</strong>
                    </div>
                    <div className="approver-summary__card">
                        <span>On Hold</span>
                        <strong>{summary.hold}</strong>
                    </div>
                    <div className="approver-summary__card">
                        <span>Rejected</span>
                        <strong>{summary.rejected}</strong>
                    </div>
                    <div className="approver-summary__card approver-summary__card--risk">
                        <span>High Risk</span>
                        <strong>{summary.highRisk}</strong>
                    </div>
                </div>
            </div>

            <div className="workspace-panel approver-tabs" role="tablist" aria-label="Approver queues">
                {APPROVER_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`ghost-button approver-tabs__button${activeTab === tab.key ? " is-active" : ""}`}
                        onClick={() => selectTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="workspace-panel approver-table-panel">
                <div className="users-form__header">
                    <div>
                        <h3>{APPROVER_TABS.find((tab) => tab.key === activeTab)?.label}</h3>
                        <p>Select an invoice to review actions, conditions, and audit history.</p>
                    </div>
                </div>
                {activeTab === "pending" && loadingWorkflows ? (
                    <div className="config-note">Loading pending approval workflows...</div>
                ) : null}
                {activeTab === "pending" && workflowError ? (
                    <div className="config-note config-note--error">{workflowError}</div>
                ) : null}
                <div className="approver-table-wrap">
                    <table className="approver-table">
                        <thead>
                            <tr>
                                <th>Invoice Id</th>
                                <th>Invoice No.</th>
                                <th>Vendor Name (OCR)</th>
                                <th>Vendor Code</th>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>PDF</th>
                                <th>Material/Service</th>
                                <th>PO/Non PO</th>
                                {activeTab === "hold" ? (
                                    <>
                                        <th>Hold By</th>
                                        <th>Pending With</th>
                                    </>
                                ) : null}
                                {activeTab === "rejected" ? <th>Rejected By</th> : null}
                                <th>Risk</th>
                                <th>Next Approver</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row) => (
                                <tr key={row.id} className={selectedRow?.id === row.id ? "is-selected" : ""}>
                                    <td>{row.invoiceId}</td>
                                    <td>{row.invoiceNo}</td>
                                    <td>{row.vendorNameOcr}</td>
                                    <td>{row.vendorCode}</td>
                                    <td>{row.invoiceDate}</td>
                                    <td>{formatAmount(row.invoiceAmount)}</td>
                                    <td>
                                        {row.rawWorkflow?.uniqueName ? (
                                            <button
                                                type="button"
                                                className="pdf-view-button"
                                                onClick={() => openInvoicePdf(row.invoiceId)}
                                            >
                                                View PDF
                                            </button>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td>{row.materialOrService}</td>
                                    <td>{row.invoiceType}</td>
                                    {activeTab === "hold" ? (
                                        <>
                                            <td>{row.holdBy || "-"}</td>
                                            <td>{row.pendingWith || "-"}</td>
                                        </>
                                    ) : null}
                                    {activeTab === "rejected" ? <td>{row.rejectedBy || "-"}</td> : null}

                                    <td>

                                        {row.highRisk === "High Risk" ? (
                                            <button
                                                type="button"
                                                className="approver-risk approver-risk-button is-high"
                                                onClick={() => {
                                                    setSelectedRowId(row.id);
                                                    setActiveModal("highRisk");
                                                }}
                                            >
                                                {row.highRisk}
                                            </button>
                                        ) : (
                                            <span className="approver-risk">
                                                {row.highRisk}
                                            </span>
                                        )}
                                    </td>

                                    <td>{row.nextApprover || "-"}</td>

                                    <td className="approver-action-icons">
                                        <button
                                            type="button"
                                            className="secondary-button"
                                            title="Invoice Summary"
                                            onClick={() => {
                                                setSelectedRowId(row.id);
                                                setActiveModal("summary");
                                            }}
                                        >
                                            📄
                                        </button>

                                        <button
                                            type="button"
                                            className="secondary-button"
                                            title="Decision"
                                            onClick={() => {
                                                setSelectedRowId(row.id);
                                                setActiveModal("decision");
                                            }}
                                        >
                                            ⚙️
                                        </button>

                                        <button
                                            type="button"
                                            className="secondary-button"
                                            title="Approval Context"
                                            onClick={() => {
                                                setSelectedRowId(row.id);
                                                setActiveModal("context");
                                            }}
                                        >
                                            ℹ️
                                        </button>
                                        <button
                                            type="button"
                                            className="secondary-button"
                                            title="Previous Transactions"
                                            onClick={() => {
                                                setSelectedRowId(row.id);
                                                setActiveModal("transactions");
                                            }}
                                        >
                                            ⤵️
                                        </button>

                                        <button
                                            type="button"
                                            className="secondary-button"
                                            title="Audit Trail"
                                            onClick={() => {
                                                setSelectedRowId(row.id);
                                                setActiveModal("audit");
                                            }}
                                        >
                                            🧾
                                        </button>
                                    </td>


                                </tr>
                            ))}
                            {!visibleRows.length ? (
                                <tr>
                                    <td colSpan={tableColumnCount}>No invoices found for this queue.</td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Removed persistent Invoice Review Workspace and Approval Context panels
                Content is available via Action column buttons (Decision modal, Context, Transactions, Audit) */}

            {activeModal && selectedRow ? (
                <div className="approver-modal-backdrop" role="presentation" onClick={() => setActiveModal("")}>
                    <div className="approver-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
                        <div className="users-form__header">
                            <div>

                                <h3>
                                    {activeModal === "summary"
                                        ? "Invoice Summary"
                                        : activeModal === "decision"
                                            ? "Decision"
                                            : activeModal === "context"
                                                ? "Approval Context"
                                                : activeModal === "transactions"
                                                    ? "Previous Transactions"
                                                    : activeModal === "audit"
                                                        ? "Audit Trail"
                                                        : "High Risk Analysis"}
                                </h3>

                                <p>{selectedRow.invoiceNo}</p>
                            </div>
                            <button type="button" className="secondary-button" onClick={() => setActiveModal("")}>
                                Close
                            </button>
                        </div>

                        {renderModalContent()}
                    </div>
                </div>
            ) : null}
        </section>
    );
}
